import { Button, ButtonLink } from '@/components/ui/button';
import { CreditCard, ExternalLink, LogOut, ShieldAlert, User } from 'lucide-react';
import { useState  } from 'react';
import type {FormEvent} from 'react';
import { useNavigate } from 'react-router-dom';
import { Page, PageHeader } from '@/components/layout/page';
import { EntitlementSummary } from '@/components/payment/entitlement-summary';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardBody, CardHeader, Container, DataList, DataRow, Section } from '@/components/ui/primitives';
import { siteOrigin } from '@/config/env';
import { useSession } from '@/hooks/use-session';
import { useSite } from '@/hooks/use-site';
import { dashlite } from '@/lib/dashlite';
import { fieldErrors, presentError } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { paths } from '@/lib/paths';

/**
 * Espace personnel.
 *
 * Il rassemble tout ce qu'un utilisateur final peut faire sur son compte :
 *
 * | Action                       | Endpoint                 |
 * | ---------------------------- | ------------------------ |
 * | Voir son profil et son accès | `GET /me`                |
 * | Rafraîchir son droit d'accès | `GET /entitlement`       |
 * | Changer son mot de passe     | `POST /auth/password`    |
 * | Gérer son abonnement         | `POST /billing/portal`   |
 * | Se déconnecter               | `POST /auth/logout`      |
 * | Supprimer son compte (RGPD)  | `DELETE /me`             |
 *
 * La page est protégée par `RequireAuth` (voir `App.tsx`) : un visiteur non
 * connecté est renvoyé vers la connexion, avec l'adresse d'origine mémorisée.
 */
export default function AccountPage() {
    const { user, entitlement, logout } = useSession();
    const navigate = useNavigate();

    if (!user) {
        return null;
    }

    return (
        <Page title="Mon compte">
            <PageHeader
                eyebrow="Espace personnel"
                title="Mon compte"
                description="Votre accès, votre abonnement et vos paramètres de sécurité."
            >
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={async () => {
                        await logout();
                        navigate(paths.home, { replace: true });
                    }}
                >
                    <LogOut className="size-4" aria-hidden />
                    Se déconnecter
                </Button>
            </PageHeader>

            <Section>
                <Container className="grid items-start gap-8 lg:grid-cols-[1.4fr_1fr]">
                    <div className="space-y-6">
                        <Card>
                            <CardHeader title="Profil" icon={<User className="size-4" />} />
                            <CardBody>
                                <DataList>
                                    <DataRow label="Adresse email" value={user.email} />
                                    <DataRow label="Compte créé le" value={formatDateTime(user.created_at)} />
                                    <DataRow label="Identifiant" value={<span className="font-mono">#{user.id}</span>} />
                                </DataList>
                            </CardBody>
                        </Card>

                        {entitlement ? (
                            <EntitlementSummary entitlement={entitlement} />
                        ) : (
                            <Alert tone="info" title="Aucun accès actif">
                                Votre compte est bien créé, mais aucun droit d'accès n'est ouvert.
                                <ButtonLink to={paths.pricing} size="sm" className="mt-3">
                                    Voir l'offre
                                </ButtonLink>
                            </Alert>
                        )}

                        <BillingPortalCard />
                        <ChangePasswordCard />
                        
                    </div>

                    <div className="space-y-6 lg:sticky lg:top-28">
                        <DeleteAccountCard />
                        
                    </div>



                </Container>
            </Section>
        </Page>
    );
}

/**
 * Portail de facturation Stripe — `POST /billing/portal`.
 *
 * Le portail permet au client de mettre à jour sa carte ou de résilier en
 * libre-service, sans un seul écran à développer de votre côté.
 *
 * Le `return_url` doit appartenir aux domaines déclarés du site : c'est
 * pourquoi il est construit depuis l'origine réelle du navigateur plutôt
 * qu'écrit en dur. Une adresse hors liste renvoie
 * `422 invalid_redirect_url`.
 */
function BillingPortalCard() {
    const { entitlement } = useSession();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const isSubscription =
        entitlement?.offer?.type === 'subscription' || entitlement?.offer?.type === 'trial_then_subscription';

    if (!entitlement || !isSubscription) {
        return null;
    }

    async function openPortal(): Promise<void> {
        setLoading(true);
        setError(null);

        try {
            const { portal_url } = await dashlite.createBillingPortal({
                return_url: `${siteOrigin()}${paths.account}`,
            });

            // Redirection plein écran : le portail est une page Stripe, pas un
            // composant à intégrer. `window.location.assign` conserve
            // l'historique du navigateur, `replace` l'effacerait.
            window.location.assign(portal_url);
        } catch (caught) {
            setError(caught);
            setLoading(false);
        }
    }

    return (
        <Card>
            <CardHeader
                title="Gérer mon abonnement"
                description="Mettre à jour la carte, consulter les factures ou résilier."
                icon={<CreditCard className="size-4" />}
            />
            <CardBody className="space-y-4">
                <p className="text-ink-soft text-sm leading-relaxed">
                    Le portail sécurisé de Stripe s'ouvre dans la foulée. Après une résiliation, votre accès reste
                    ouvert jusqu'à la fin de la période déjà réglée.
                </p>

                {error !== null && (
                    <Alert tone="critical" title={presentError(error).message}>
                        {presentError(error).hint}
                    </Alert>
                )}

                <Button onClick={openPortal} loading={loading} variant="secondary">
                    <ExternalLink className="size-4" aria-hidden />
                    Ouvrir le portail de facturation
                </Button>
            </CardBody>
        </Card>
    );
}

/**
 * Changement de mot de passe — `POST /auth/password`.
 *
 * Effet de bord important : **toutes les sessions de l'utilisateur sont
 * fermées**. L'API renvoie un nouveau token, qui reconnecte l'appareil
 * courant — le SDK l'applique automatiquement. Sans cela, l'utilisateur
 * serait déconnecté de l'appareil depuis lequel il vient d'agir.
 */
function ChangePasswordCard() {
    const [currentPassword, setCurrentPassword] = useState('');
    const [password, setPassword] = useState('');
    const [confirmation, setConfirmation] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const [localError, setLocalError] = useState<string | null>(null);

    const fields = fieldErrors(error);

    async function handleSubmit(event: FormEvent): Promise<void> {
        event.preventDefault();
        setLocalError(null);
        setDone(false);

        if (password !== confirmation) {
            setLocalError('Les deux mots de passe saisis ne sont pas identiques.');

            return;
        }

        setSubmitting(true);
        setError(null);

        try {
            await dashlite.changePassword({ current_password: currentPassword, password });
            setDone(true);
            setCurrentPassword('');
            setPassword('');
            setConfirmation('');
        } catch (caught) {
            setError(caught);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Card>
            <CardHeader title="Mot de passe" description="Changer le mot de passe de votre compte." />
            <CardBody>
                <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                    <Input
                        label="Mot de passe actuel"
                        type="password"
                        required
                        autoComplete="current-password"
                        value={currentPassword}
                        error={fields.current_password}
                        onChange={(event) => setCurrentPassword(event.target.value)}
                    />

                    <div className="grid gap-4 sm:grid-cols-2">
                        <Input
                            label="Nouveau mot de passe"
                            type="password"
                            required
                            minLength={8}
                            autoComplete="new-password"
                            value={password}
                            error={fields.password}
                            onChange={(event) => setPassword(event.target.value)}
                        />

                        <Input
                            label="Confirmation"
                            type="password"
                            required
                            autoComplete="new-password"
                            value={confirmation}
                            error={localError ?? undefined}
                            onChange={(event) => setConfirmation(event.target.value)}
                        />
                    </div>

                    {done && (
                        <Alert tone="positive" title="Mot de passe modifié">
                            Vos autres sessions ont été fermées. Celle-ci reste ouverte grâce au nouveau token renvoyé
                            par l'API.
                        </Alert>
                    )}

                    {error !== null && Object.keys(fields).length === 0 && (
                        <Alert tone="critical" title={presentError(error).message}>
                            {presentError(error).hint}
                        </Alert>
                    )}

                    <Button type="submit" loading={submitting} variant="secondary">
                        Modifier le mot de passe
                    </Button>
                </form>
            </CardBody>
        </Card>
    );
}

/**
 * Suppression de compte (RGPD) — `DELETE /me`.
 *
 * Action irréversible : on demande une confirmation explicite, saisie au
 * clavier, plutôt qu'un simple `confirm()` du navigateur — un dialogue natif
 * bloque la page et se clique sans réfléchir.
 */
function DeleteAccountCard() {
    const site = useSite();
    const { deleteAccount } = useSession();
    const navigate = useNavigate();

    const [confirmation, setConfirmation] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<unknown>(null);

    const CONFIRMATION_WORD = 'SUPPRIMER';

    async function handleDelete(): Promise<void> {
        setSubmitting(true);
        setError(null);

        try {
            await deleteAccount();
            navigate(paths.home, { replace: true });
        } catch (caught) {
            setError(caught);
            setSubmitting(false);
        }
    }

    return (
        <Card className="border-critical/30">
            <CardHeader
                title="Supprimer mon compte"
                description="Suppression définitive de votre compte et de vos données."
                icon={<ShieldAlert className="text-critical size-4" />}
            />
            <CardBody className="space-y-4">
                <p className="text-ink-soft text-sm leading-relaxed">
                    Cette action est irréversible. Votre accès est immédiatement révoqué et vos données sont
                    supprimées. Un abonnement en cours doit être résilié séparément
                    {site.contact_email ? (
                        <>
                            {' '}
                            — écrivez-nous à{' '}
                            <a href={`mailto:${site.contact_email}`} className="text-brand-700 underline">
                                {site.contact_email}
                            </a>{' '}
                            en cas de doute.
                        </>
                    ) : (
                        '.'
                    )}
                </p>

                <Input
                    label={`Saisissez « ${CONFIRMATION_WORD} » pour confirmer`}
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    autoComplete="off"
                    
                />

                {error !== null && (
                    <Alert tone="critical" title={presentError(error).message}>
                        {presentError(error).hint}
                    </Alert>
                )}

                <Button
                    variant="danger"
                    loading={submitting}
                    disabled={confirmation !== CONFIRMATION_WORD}
                    onClick={handleDelete}
                    className="inline-flex items-center justify-center rounded-[var(--radius)] border border-[var(--border)] bg-[var(--background)] px-5 py-2.5 text-sm font-medium text-[var(--foreground)] transition-all hover:bg-[var(--secondary)] active:scale-[0.98]"
                >
                    Supprimer définitivement mon compte
                </Button>
            </CardBody>
        </Card>
    );
}
